import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import ProductCard from "../components/ProductCard";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { api } from "../lib/api";

export default function Catalog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState(searchParams.get("category") || "All");
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(["All", ...data.categories])).catch(() => {});
  }, []);

  // Sync when URL params change (nav search / mega menu)
  useEffect(() => {
    setActive(searchParams.get("category") || "All");
    setQuery(searchParams.get("q") || "");
  }, [searchParams]);

  useEffect(() => {
    setLoading(true);
    const handle = setTimeout(() => {
      const params = {};
      if (active !== "All") params.category = active;
      if (query.trim()) params.q = query.trim();
      api.get("/products", { params }).then(({ data }) => setProducts(data)).catch(() => {}).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(handle);
  }, [active, query]);

  const selectCategory = (c) => {
    setActive(c);
    const next = new URLSearchParams(searchParams);
    if (c === "All") next.delete("category"); else next.set("category", c);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="pt-20 bg-[#0F0F10]">
      <SEO
        title={active !== "All" ? `${active} Research Peptides` : "Research Peptide Catalog"}
        description={
          active !== "All"
            ? `Shop premium ${active.toLowerCase()} research peptides at EndaG1. Independently verified, COA included. Research use only.`
            : "Browse EndaG1's full catalog of pharmaceutical-grade peptides — BPC-157, TB-500, Tirzepatide, Retatrutide, GHK-Cu, Semax, Selank and more. COA with every batch."
        }
        canonicalPath={active !== "All" ? `/products?category=${encodeURIComponent(active)}` : "/products"}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://endag1.com/" },
              { "@type": "ListItem", position: 2, name: "Products", item: "https://endag1.com/products" },
              ...(active !== "All"
                ? [{ "@type": "ListItem", position: 3, name: active, item: `https://endag1.com/products?category=${encodeURIComponent(active)}` }]
                : []),
            ],
          },
        ]}
      />
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 pt-16 pb-10 border-b border-white/10">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[12px] font-semibold uppercase tracking-[0.2em] text-[#4ADE80] mb-4">
          {products.length} Compounds · Pharmaceutical Grade
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="font-display font-extrabold text-4xl sm:text-5xl md:text-6xl tracking-tighter">Full Catalog</motion.h1>

        <div className="mt-8 flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
          <div data-testid="category-filters" className="flex flex-wrap gap-2.5">
            {categories.map((c) => (
              <button key={c} data-testid={`filter-${c.toLowerCase().replace(/\s|&/g, "-")}`} onClick={() => selectCategory(c)}
                className={`btn-press text-[12px] font-medium rounded-full px-5 py-2.5 border transition-[background-color,border-color,color] ${
                  active === c ? "bg-[#4ADE80] text-[#0F0F10] border-[#4ADE80]" : "bg-[#0F0F10] border-white/10 text-[#A8A8A8] hover:border-white/25 hover:text-[#F5F5F5]"
                }`}>
                {c}
              </button>
            ))}
          </div>
          <div className="relative w-full lg:w-72">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7a7a7a]" />
            <input data-testid="product-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search compounds…"
              className="w-full rounded-full bg-[#171717] border border-white/15 pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#4ADE80] focus:border-transparent transition-[box-shadow] placeholder:text-[#7a7a7a]" />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-14 min-h-[50vh]">
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="skeleton rounded-[24px] aspect-[3/4]" />)}
          </div>
        ) : products.length === 0 ? (
          <div data-testid="no-results" className="rounded-[24px] border border-white/10 bg-[#1F1F1F] p-16 text-center text-sm uppercase tracking-widest text-[#7a7a7a]">
            No compounds match your search.
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
          </div>
        )}
      </section>
      <Footer />
      <div className="h-16 lg:hidden" aria-hidden="true" />
    </div>
  );
}
